import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, Crown, Plus, Save, Shield, Trash2, UserPlus } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Checkbox } from './ui/checkbox';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { ScrollArea } from './ui/scroll-area';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from './ui/collapsible';
import { AdminPageHeader, adminPageStackClass } from './AdminChrome';

/** Avatar depuis le champ `photo` de prysma_bdd.users (URL ou data URL). */
const UserPrysmaAvatar = ({ photo, label, theme, sizeClass = 'h-11 w-11' }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const showPhoto = typeof photo === 'string' && photo.trim() && !imgFailed;
  const initial = (label || '?').trim().charAt(0).toUpperCase() || '?';
  return (
    <div
      className={`relative shrink-0 overflow-hidden rounded-full border-2 ${sizeClass} flex items-center justify-center`}
      style={{ borderColor: theme.border, backgroundColor: theme.panelInset }}
    >
      {showPhoto ? (
        <img
          src={photo.trim()}
          alt=""
          className="h-full w-full object-cover"
          onError={() => setImgFailed(true)}
        />
      ) : (
        <span
          className="flex h-full w-full items-center justify-center text-sm font-semibold text-white"
          style={{ backgroundColor: theme.accent }}
          aria-hidden
        >
          {initial}
        </span>
      )}
    </div>
  );
};

const PermissionsPage = ({ token, apiBase, theme, access, onUnauthorized }) => {
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const canAssign = access?.is_super_admin || access?.permissions?.includes('permissions.assign');
  const canManageScope =
    access?.is_super_admin || access?.permissions?.includes('permissions.manage_scope');

  const [catalog, setCatalog] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingUser, setSavingUser] = useState(null);
  const [drafts, setDrafts] = useState({});
  const [showAdd, setShowAdd] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [userSearch, setUserSearch] = useState('');
  const [loadingUsers, setLoadingUsers] = useState(false);
  /** false = panneau « thèmes » replié ; absent/true = déplié (défaut ouvert) */
  const [userThemesOpen, setUserThemesOpen] = useState({});

  const allIds = useMemo(() => {
    const ids = [];
    (catalog || []).forEach((g) => {
      (g.permissions || []).forEach((p) => ids.push(p.id));
    });
    return ids;
  }, [catalog]);

  const handleApiError = useCallback(
    (error) => {
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      const msg = error.response?.data?.detail || error.message || 'Erreur';
      toast.error(typeof msg === 'string' ? msg : 'Erreur serveur');
    },
    [onUnauthorized]
  );

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catRes, subRes, sitesRes] = await Promise.all([
        axios.get(`${apiBase}/admin/permissions/catalog`, { headers: authHeaders }),
        axios.get(`${apiBase}/admin/permission-subjects`, { headers: authHeaders }),
        axios.get(`${apiBase}/admin/sites`, { headers: authHeaders }),
      ]);
      setCatalog(catRes.data.groups || []);
      setSubjects(subRes.data || []);
      const sl = Array.isArray(sitesRes.data) ? sitesRes.data : [];
      setSites(sl.map((s) => ({ id: String(s.id), nom: s.nom || '—' })));
    } catch (e) {
      handleApiError(e);
      setCatalog([]);
      setSubjects([]);
    } finally {
      setLoading(false);
    }
  }, [apiBase, authHeaders, handleApiError]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const next = {};
    subjects.forEach((s) => {
      const perms = Array.isArray(s.permissions) ? s.permissions : null;
      const eff =
        perms == null || s.is_super_admin
          ? allIds
          : perms.filter((p) => allIds.includes(p));
      next[s.username] = {
        permissions: new Set(eff),
        scopeAll: s.allowed_entreprise_ids == null,
        scopeIds: Array.isArray(s.allowed_entreprise_ids) ? [...s.allowed_entreprise_ids] : [],
        is_super_admin: !!s.is_super_admin,
      };
    });
    setDrafts(next);
  }, [subjects, allIds]);

  const openAddModal = async () => {
    if (!canAssign) return;
    setShowAdd(true);
    setUserSearch('');
    setLoadingUsers(true);
    try {
      const { data } = await axios.get(`${apiBase}/admin/users`, { headers: authHeaders });
      setAllUsers(Array.isArray(data) ? data : []);
    } catch (e) {
      handleApiError(e);
      setAllUsers([]);
    } finally {
      setLoadingUsers(false);
    }
  };

  const addAccess = async (username) => {
    try {
      await axios.post(
        `${apiBase}/admin/add-administrator`,
        { username },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      toast.success('Accès back-office ajouté (droits complets par défaut — à ajuster ci-dessous)');
      setShowAdd(false);
      await loadData();
    } catch (e) {
      handleApiError(e);
    }
  };

  const removeAccess = async (username, label) => {
    if (!canAssign) return;
    if (!window.confirm(`Retirer l’accès back-office pour ${label} ?`)) return;
    try {
      await axios.delete(`${apiBase}/admin/remove-administrator/${encodeURIComponent(username)}`, {
        headers: authHeaders,
      });
      toast.success('Accès retiré');
      await loadData();
    } catch (e) {
      handleApiError(e);
    }
  };

  const grantAllPermissions = (username) => {
    setDrafts((prev) => {
      const d = prev[username];
      if (!d || d.is_super_admin) return prev;
      return {
        ...prev,
        [username]: { ...d, permissions: new Set(allIds) },
      };
    });
    toast.success('Toutes les permissions sont cochées. Cliquez sur « Enregistrer » pour appliquer.');
  };

  const groupPermCounts = (username, group) => {
    const d = drafts[username];
    const ids = (group.permissions || []).map((p) => p.id);
    if (!d) return { checked: 0, total: ids.length };
    const checked = ids.filter((id) => d.permissions.has(id)).length;
    return { checked, total: ids.length };
  };

  const toggleDraftPerm = (username, permId) => {
    setDrafts((prev) => {
      const d = prev[username];
      if (!d || d.is_super_admin) return prev;
      const next = new Set(d.permissions);
      if (next.has(permId)) next.delete(permId);
      else next.add(permId);
      return { ...prev, [username]: { ...d, permissions: next } };
    });
  };

  /** Coche ou décoche toutes les permissions d’un groupe (catégorie) pour l’utilisateur. */
  const setGroupPermissionsAll = (username, group, checked) => {
    setDrafts((prev) => {
      const d = prev[username];
      if (!d || d.is_super_admin) return prev;
      const ids = (group.permissions || []).map((p) => p.id).filter(Boolean);
      if (ids.length === 0) return prev;
      const next = new Set(d.permissions);
      ids.forEach((id) => {
        if (checked) next.add(id);
        else next.delete(id);
      });
      return { ...prev, [username]: { ...d, permissions: next } };
    });
  };

  const setScopeAll = (username, all) => {
    setDrafts((prev) => {
      const d = prev[username];
      if (!d) return prev;
      return { ...prev, [username]: { ...d, scopeAll: all, scopeIds: all ? [] : d.scopeIds } };
    });
  };

  const toggleScopeEnt = (username, id) => {
    if (!canManageScope) return;
    setDrafts((prev) => {
      const d = prev[username];
      if (!d) return prev;
      const set = new Set(d.scopeIds);
      if (set.has(id)) set.delete(id);
      else set.add(id);
      return { ...prev, [username]: { ...d, scopeIds: [...set], scopeAll: false } };
    });
  };

  const saveUser = async (username) => {
    if (!canAssign) return;
    const d = drafts[username];
    if (!d || d.is_super_admin) return;
    setSavingUser(username);
    try {
      await axios.put(
        `${apiBase}/admin/permissions/users/${encodeURIComponent(username)}`,
        {
          permissions: [...d.permissions],
          allowed_entreprise_ids: d.scopeAll ? null : d.scopeIds,
        },
        { headers: { ...authHeaders, 'Content-Type': 'application/json' } }
      );
      toast.success('Permissions enregistrées');
      await loadData();
    } catch (e) {
      handleApiError(e);
    } finally {
      setSavingUser(null);
    }
  };

  const existingUsernames = useMemo(() => new Set(subjects.map((s) => s.username)), [subjects]);

  const filteredPickUsers = useMemo(() => {
    const q = userSearch.trim().toLowerCase();
    return allUsers.filter((u) => {
      if (existingUsernames.has(u.username)) return false;
      if (!q) return true;
      const n = (u.display_name || u.username || '').toLowerCase();
      return n.includes(q) || (u.username || '').toLowerCase().includes(q);
    });
  }, [allUsers, userSearch, existingUsernames]);

  if (!canAssign && !(access?.permissions || []).includes('permissions.access')) {
    return (
      <div
        className="rounded-2xl border border-dashed p-10 text-center text-sm max-w-lg mx-auto"
        style={{ borderColor: theme.border, color: theme.textSecondary, backgroundColor: theme.panelInset }}
      >
        Vous n’avez pas accès à cette page.
      </div>
    );
  }

  return (
    <div className={adminPageStackClass}>
      <AdminPageHeader
        theme={theme}
        icon={Shield}
        title="Permissions"
        subtitle="Droits par module : utilisez le chevron à côté de chaque nom pour déplier les thèmes et le périmètre. Les comptes super-admin en base ne sont pas modifiables ici."
        actions={
          canAssign ? (
            <Button
              type="button"
              size="sm"
              className="gap-1.5 shrink-0 rounded-xl shadow-sm"
              style={{ backgroundColor: theme.accent, color: '#fff' }}
              onClick={openAddModal}
            >
              <UserPlus className="h-4 w-4" />
              Ajouter un accès
            </Button>
          ) : null
        }
      />

      {loading ? (
        <p className="text-sm text-center py-12" style={{ color: theme.textSecondary }}>
          Chargement…
        </p>
      ) : (
        <div className="space-y-4">
          {subjects.map((s) => {
            const d = drafts[s.username];
            const isSuper = !!s.is_super_admin;
            const themesExpanded = userThemesOpen[s.username] !== false;

            const headerTitle = (
              <div className="flex min-w-0 flex-1 items-start gap-3">
                <UserPrysmaAvatar
                  photo={s.photo}
                  label={s.display_name || s.username}
                  theme={theme}
                />
                <div className="min-w-0 flex-1">
                  <CardTitle className="text-base font-semibold" style={{ color: theme.text }}>
                    {s.display_name || s.username}
                  </CardTitle>
                  <div className="text-xs mt-1 flex flex-wrap items-center gap-x-2 gap-y-1" style={{ color: theme.textSecondary }}>
                    <span>@{s.username}</span>
                    {isSuper ? (
                      <Badge className="text-[10px]" variant="secondary">
                        Super-admin
                      </Badge>
                    ) : null}
                  </div>
                </div>
              </div>
            );

            const actionButtons =
              canAssign && !isSuper ? (
                <div className="flex flex-wrap gap-1 justify-end shrink-0">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1"
                    disabled={savingUser === s.username}
                    onClick={() => grantAllPermissions(s.username)}
                    style={{ borderColor: theme.border, color: theme.text }}
                    title="Cocher toutes les permissions du catalogue"
                  >
                    <Crown className="h-3.5 w-3.5" />
                    Super administrateur
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 gap-1"
                    disabled={savingUser === s.username}
                    onClick={() => saveUser(s.username)}
                    style={{ borderColor: theme.border }}
                  >
                    <Save className="h-3.5 w-3.5" />
                    Enregistrer
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="destructive"
                    className="h-8 px-2"
                    onClick={() => removeAccess(s.username, s.display_name || s.username)}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ) : null;

            const permissionsAccordion = (
              <Accordion type="multiple" className="w-full rounded-md border px-2" style={{ borderColor: theme.border }}>
                {catalog.map((group) => {
                  const { checked, total } = groupPermCounts(s.username, group);
                  return (
                    <AccordionItem key={group.id} value={String(group.id)}>
                      <AccordionTrigger className="py-3 text-sm hover:no-underline" style={{ color: theme.text }}>
                        <span className="flex items-center gap-2 text-left">
                          <span className="font-semibold">{group.label}</span>
                          <Badge variant="secondary" className="text-[10px] font-normal tabular-nums">
                            {checked}/{total}
                          </Badge>
                        </span>
                      </AccordionTrigger>
                      <AccordionContent>
                        {canAssign && !isSuper ? (
                          <div className="flex flex-wrap items-center gap-2 pb-2 border-b mb-2" style={{ borderColor: theme.border }}>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs"
                              style={{ color: theme.accent }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setGroupPermissionsAll(s.username, group, true);
                              }}
                            >
                              Tout cocher
                            </Button>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              className="h-8 px-2 text-xs"
                              style={{ color: theme.textSecondary }}
                              onClick={(e) => {
                                e.stopPropagation();
                                setGroupPermissionsAll(s.username, group, false);
                              }}
                            >
                              Tout décocher
                            </Button>
                          </div>
                        ) : null}
                        <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3 pb-2">
                          {(group.permissions || []).map((p) => (
                            <label
                              key={p.id}
                              className="flex items-start gap-2 rounded-md border px-2.5 py-2 text-sm cursor-pointer"
                              style={{ borderColor: theme.border, color: theme.text }}
                            >
                              <Checkbox
                                checked={!!d?.permissions?.has(p.id)}
                                disabled={!canAssign}
                                onCheckedChange={() => toggleDraftPerm(s.username, p.id)}
                              />
                              <span>
                                <span className="font-medium block leading-tight">{p.label}</span>
                                <span
                                  className="text-[11px] leading-snug block mt-0.5"
                                  style={{ color: theme.textSecondary }}
                                >
                                  {p.description}
                                </span>
                              </span>
                            </label>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  );
                })}
                <AccordionItem value="__perimetre__">
                  <AccordionTrigger className="py-3 text-sm hover:no-underline" style={{ color: theme.text }}>
                    <span className="font-semibold text-left">Périmètre entreprises</span>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-3 pb-2">
                      {!canManageScope ? (
                        <p className="text-xs" style={{ color: theme.textSecondary }}>
                          Seuls les utilisateurs avec la permission « Gérer le périmètre » peuvent restreindre les entreprises
                          (ou un super-admin).
                        </p>
                      ) : null}
                      <label className="flex items-center gap-2 text-sm" style={{ color: theme.text }}>
                        <Checkbox
                          checked={!!d?.scopeAll}
                          disabled={!canManageScope}
                          onCheckedChange={(c) => setScopeAll(s.username, !!c)}
                        />
                        Toutes les entreprises (aucune restriction)
                      </label>
                      {!d?.scopeAll ? (
                        <ScrollArea className="h-40 rounded-md border p-2" style={{ borderColor: theme.border }}>
                          <div className="space-y-1">
                            {sites.map((site) => (
                              <label
                                key={site.id}
                                className="flex items-center gap-2 px-1 py-1 rounded text-sm cursor-pointer"
                                style={{ color: theme.text }}
                              >
                                <Checkbox
                                  checked={d?.scopeIds?.includes(site.id)}
                                  disabled={!canManageScope}
                                  onCheckedChange={() => toggleScopeEnt(s.username, site.id)}
                                />
                                {site.nom}
                              </label>
                            ))}
                          </div>
                        </ScrollArea>
                      ) : null}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            );

            if (isSuper) {
              return (
                <Card key={s.username} className="rounded-2xl border shadow-sm overflow-hidden" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                  <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between gap-2">
                    {headerTitle}
                    {actionButtons}
                  </CardHeader>
                  <CardContent className="px-4 pb-4 pt-0 space-y-4">
                    <p className="text-sm" style={{ color: theme.textSecondary }}>
                      Tous les droits, sans restriction de périmètre.
                    </p>
                  </CardContent>
                </Card>
              );
            }

            return (
              <Collapsible
                key={s.username}
                open={themesExpanded}
                onOpenChange={(open) =>
                  setUserThemesOpen((prev) => {
                    const next = { ...prev };
                    if (open) delete next[s.username];
                    else next[s.username] = false;
                    return next;
                  })
                }
              >
                <Card className="rounded-2xl border shadow-sm overflow-hidden" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
                  <CardHeader className="p-4 pb-2 flex flex-row items-start justify-between gap-2">
                    <div className="flex gap-2 min-w-0 flex-1 items-start">
                      <CollapsibleTrigger asChild>
                        <button
                          type="button"
                          className="rounded-md p-1.5 -ml-1 shrink-0 mt-0.5 outline-none hover:bg-muted/60 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                          style={{ color: theme.textSecondary }}
                          title={themesExpanded ? 'Replier les thèmes de permissions' : 'Déplier les thèmes de permissions'}
                          aria-expanded={themesExpanded}
                        >
                          <ChevronDown
                            className={`h-4 w-4 transition-transform duration-200 ${themesExpanded ? 'rotate-180' : ''}`}
                          />
                        </button>
                      </CollapsibleTrigger>
                      {headerTitle}
                    </div>
                    {actionButtons}
                  </CardHeader>
                  <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down">
                    <CardContent className="px-4 pb-4 pt-0 space-y-4">{permissionsAccordion}</CardContent>
                  </CollapsibleContent>
                </Card>
              </Collapsible>
            );
          })}
        </div>
      )}

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="max-w-lg" style={{ backgroundColor: theme.surface, borderColor: theme.border }}>
          <DialogHeader>
            <DialogTitle style={{ color: theme.text }}>Ajouter un accès back-office</DialogTitle>
            <DialogDescription className="sr-only">
              Recherchez un utilisateur Prysma puis ajoutez-lui un accès au back-office. Les droits pourront être ajustés
              ensuite dans la liste.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Label style={{ color: theme.text }}>Recherche</Label>
            <Input
              value={userSearch}
              onChange={(e) => setUserSearch(e.target.value)}
              placeholder="Nom ou identifiant…"
              style={{ borderColor: theme.border, color: theme.text }}
            />
            <ScrollArea className="h-64 border rounded-md" style={{ borderColor: theme.border }}>
              <div className="p-2 space-y-1">
                {loadingUsers ? (
                  <p className="text-sm p-4 text-center" style={{ color: theme.textSecondary }}>
                    Chargement…
                  </p>
                ) : filteredPickUsers.length === 0 ? (
                  <p className="text-sm p-4 text-center" style={{ color: theme.textSecondary }}>
                    Aucun utilisateur disponible.
                  </p>
                ) : (
                  filteredPickUsers.map((u) => (
                    <div
                      key={u.username}
                      className="flex items-center justify-between gap-2 p-2 rounded-md"
                      style={{ backgroundColor: theme.background }}
                    >
                      <div className="flex min-w-0 flex-1 items-center gap-2">
                        <UserPrysmaAvatar
                          photo={u.photo}
                          label={u.display_name || u.username}
                          theme={theme}
                          sizeClass="h-9 w-9"
                        />
                        <div className="min-w-0">
                          <p className="text-sm font-medium truncate" style={{ color: theme.text }}>
                            {u.display_name || u.username}
                          </p>
                          <p className="text-xs truncate" style={{ color: theme.textSecondary }}>
                            @{u.username}
                          </p>
                        </div>
                      </div>
                      <Button type="button" size="sm" className="shrink-0 gap-1" onClick={() => addAccess(u.username)}>
                        <Plus className="h-3.5 w-3.5" />
                        Ajouter
                      </Button>
                    </div>
                  ))
                )}
              </div>
            </ScrollArea>
            <p className="text-[11px] leading-relaxed" style={{ color: theme.textSecondary }}>
              L’utilisateur reçoit par défaut toutes les permissions ; affinez-les ensuite dans la liste.
            </p>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PermissionsPage;
